import React from 'react';

import Avatar from 'common-ui/chat/Avatar';
import { formatChatListUnreadCount } from 'common-ui/chat/constants';
import { formatChatListTime } from 'utils/dateUtils';
import { getEmailPreview, parseEmailMessage } from 'utils/emailUtils';

import {
  ChatListBlock,
  ChatListItem,
  ChatListItemBlock,
  ChatListItemLeftSide,
  ChatListItemRightSide,
  ChatListItemText,
  ChatListItemTime,
  ChatListItemTitle,
  ChatListItemTitleBlock,
  UnreadBadge,
} from './ChatList.styles';
import FacebookChatList from './FacebookChatList';
import InstagramChatList from './InstagramChatList';

const getSenderName = (sender) => {
  if (sender?.name && sender?.surname) {
    return `${sender.name} ${sender.surname}`;
  }
  if (sender?.name) {
    return sender.name;
  }
  return `User ${sender?.id?.slice(-6) || 'Unknown'}`;
};

const getMessagePreview = (conversation) => {
  if (conversation.platform === 'imap') {
    const messageData = conversation.lastMessageObject || {
      messageText: conversation.lastMessage,
      platform: 'imap',
    };

    const emailData = parseEmailMessage(messageData);
    if (emailData?.subject && emailData?.body) {
      const bodyPreview = getEmailPreview(emailData.body, 50);
      return `${emailData.subject}: ${bodyPreview}`;
    } else if (emailData?.subject) {
      return emailData.subject;
    }
  }

  return conversation.lastMessage || 'No messages yet';
};

const ChatList = ({ conversations = [], loading, onConversationSelect, platform }) => {
  if (platform === 'facebook') {
    return <FacebookChatList onConversationSelect={onConversationSelect} limit={10} />;
  }

  if (platform === 'instagram') {
    return <InstagramChatList onConversationSelect={onConversationSelect} limit={10} />;
  }

  if (loading) {
    return (
      <ChatListBlock>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>Loading conversations...</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  if (conversations.length === 0) {
    return (
      <ChatListBlock>
        <ChatListItem>
          <ChatListItemBlock>
            <ChatListItemText>Select a platform to view conversations</ChatListItemText>
          </ChatListItemBlock>
        </ChatListItem>
      </ChatListBlock>
    );
  }

  return (
    <ChatListBlock>
      {conversations.map((conversation) => {
        const hasUnread = conversation.unreadCount > 0;

        return (
          <ChatListItem
            key={conversation.conversationId}
            onClick={() => onConversationSelect?.(conversation)}
          >
            <ChatListItemBlock>
              <ChatListItemLeftSide>
                <Avatar
                  name={getSenderName(conversation.sender)}
                  src={conversation.sender?.avatar}
                  platform={conversation.platform || 'whatsapp'}
                  size={36}
                />
                <ChatListItemTitleBlock>
                  <ChatListItemTitle>{getSenderName(conversation.sender)}</ChatListItemTitle>
                  <ChatListItemText $hasUnread={hasUnread}>
                    {getMessagePreview(conversation)}
                  </ChatListItemText>
                </ChatListItemTitleBlock>
              </ChatListItemLeftSide>
              <ChatListItemRightSide>
                {hasUnread && (
                  <UnreadBadge>
                    {formatChatListUnreadCount(conversation.unreadCount)}
                  </UnreadBadge>
                )}
                <ChatListItemTime>
                  {formatChatListTime(conversation.lastMessageAt)}
                </ChatListItemTime>
              </ChatListItemRightSide>
            </ChatListItemBlock>
          </ChatListItem>
        );
      })}
    </ChatListBlock>
  );
};

export default ChatList;
